/**
 * 연동 키 발급·수정 창 — 「키 발급 흐름」 1, D-7, D-9, D-11.
 * @spec:2026-10-14-phai-admin-외부연동키-관리화면
 *
 * 발급: 이름, 허용 IP(한 줄에 하나), 「상대 서버에 바로 적용」(자동 전달 주소가 있을 때만, 기본 체크).
 * 수정: 이름, 허용 IP만.
 */
import React, { useState } from "react";
import Button from "components/atoms/Button";
import type { ApiKeyView } from "api/partnerAdminService";
import {
  IP_MAX,
  REL_NOTICE,
  WILDCARD_IP_WARNING,
  parseLines,
  validateIpAllowlist,
  validateLabel,
} from "utils/partnerAdmin";
import {
  CheckLabel,
  ErrorMsg,
  Field,
  FieldLabel,
  Hint,
  ModalBody,
  ModalFooter,
  ModalTitle,
  Notice,
  TextArea,
  TextInput,
} from "./style";

export interface ApiKeyFormValues {
  label: string;
  ipAllowlist: string[];
  /** 발급 + 자동 전달 주소가 있을 때만 값이 있다 */
  provision?: boolean;
}

interface ApiKeyFormModalProps {
  /** 없으면 발급, 있으면 수정 */
  apiKey?: ApiKeyView;
  /** 파트너에 자동 전달 주소가 있는가(발급 때만 의미) */
  canProvision: boolean;
  isRel: boolean;
  onSubmit: (values: ApiKeyFormValues) => Promise<void>;
  onCancel: () => void;
}

const ApiKeyFormModal: React.FC<ApiKeyFormModalProps> = ({ apiKey, canProvision, isRel, onSubmit, onCancel }) => {
  const isEdit = !!apiKey;
  const [label, setLabel] = useState(apiKey?.label ?? "");
  const [ipText, setIpText] = useState((apiKey?.ipAllowlist ?? []).join("\n"));
  const [provision, setProvision] = useState(true);
  const [labelError, setLabelError] = useState<string>();
  const [ipError, setIpError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);

  const ips = parseLines(ipText);
  const hasWildcard = ips.includes("*");

  const handleSubmit = async () => {
    const labelCheck = validateLabel(label);
    const ipCheck = validateIpAllowlist(ips);
    setLabelError(labelCheck.ok ? undefined : labelCheck.message);
    setIpError(ipCheck.ok ? undefined : ipCheck.message);
    if (!labelCheck.ok || !ipCheck.ok) return;

    const values: ApiKeyFormValues = { label: label.trim(), ipAllowlist: ips };
    if (!isEdit && canProvision) values.provision = provision;

    setSubmitting(true);
    try {
      await onSubmit(values);
    } catch {
      // 실패 사유는 훅이 토스트로 보여준다. 창은 그대로 둔다.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalBody data-testid="api-key-form">
      <ModalTitle>{isEdit ? "연동 키 수정" : "연동 키 발급"}</ModalTitle>
      {isRel && !isEdit && <Notice $tone="warn">{REL_NOTICE}</Notice>}

      <Field>
        <FieldLabel>이름</FieldLabel>
        <TextInput
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="예: kmrs-dev-2026-10"
          data-testid="api-key-form-label"
        />
        {labelError && <ErrorMsg>{labelError}</ErrorMsg>}
      </Field>

      <Field>
        <FieldLabel>허용 IP</FieldLabel>
        <TextArea
          value={ipText}
          onChange={(e) => setIpText(e.target.value)}
          placeholder={"한 줄에 하나씩\n예: 3.39.10.20"}
          data-testid="api-key-form-ips"
        />
        <Hint>
          IPv4·IPv6 주소를 정확히 적습니다(CIDR 범위는 쓸 수 없습니다). 최대 {IP_MAX}개. 모든 IP를 허용하려면 *를
          적습니다.
        </Hint>
        {hasWildcard && (
          <Notice $tone="warn" data-testid="api-key-form-wildcard-warning">
            {WILDCARD_IP_WARNING}
          </Notice>
        )}
        {ipError && <ErrorMsg data-testid="api-key-form-ip-error">{ipError}</ErrorMsg>}
      </Field>

      {!isEdit && canProvision && (
        <CheckLabel>
          <input
            type="checkbox"
            checked={provision}
            onChange={(e) => setProvision(e.target.checked)}
            data-testid="api-key-form-provision"
          />
          상대 서버에 바로 적용
        </CheckLabel>
      )}

      {isEdit && <Hint>허용 IP를 바꾸면 다음 요청부터 바로 적용됩니다.</Hint>}

      <ModalFooter>
        <Button type="Text" size="Medium" variant="Ghost" color="Secondary" onClick={onCancel} disabled={submitting}>
          취소
        </Button>
        <Button
          type="Text"
          size="Medium"
          variant="Contained"
          color="Primary"
          onClick={handleSubmit}
          disabled={submitting}
          data-testid="api-key-form-submit"
        >
          {isEdit ? "저장" : "발급"}
        </Button>
      </ModalFooter>
    </ModalBody>
  );
};

export default ApiKeyFormModal;
